import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Table, Button, Input, Popconfirm, DatePicker, Select, Tag } from "antd";
import { PaperClipOutlined, PlusOutlined, HistoryOutlined } from "@ant-design/icons";
import { ReactComponent as EditIcon } from "../../../../assets/edit-icon.svg";
import { ReactComponent as SaveIcon } from "../../../../assets/save-icon.svg";
import { ReactComponent as CrossIcon } from "../../../../assets/cross-icon.svg";
import SendIcon from "../../../../assets/send.svg.png";
import AttachmentContainer from "./AttachmentContainter";
import VarianceHistoryModal from "./VarianceHistoryModal";
import CommentsModal from "./CommentsModal";
import "../index.less";
import { useLabsStock } from "../../context/LabsStockContext";
import { Avatar } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { getExpiryColor } from "../../utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { fetchVarianceStatuses } from "../../../VarianceStatuses/redux/actions";
import { fetchAllVarianceReports, updateVarianceReport, addVarianceComment, fetchVarianceHistory } from "../../redux/actions";

dayjs.extend(utc);

const VarianceReportTable = ({ registerId, record }) => {
  const { isAmending, setIsAmending, finalizedStockTakes, finalizeStockTake } = useLabsStock();
  // const { user } = useSelector((state) => state.authReducer);
  // const { allVarianceStatuses } = useSelector((state) => state.varianceStatusesReducer);
  // const { currentStockManagementRegister, varianceReports, fetchingVarianceReportsLoading } = useSelector((state) => state.labsStockManagementReducer);
  const user = useSelector((state) => state.authReducer.user);
  const allVarianceStatuses = useSelector((state) => state.varianceStatusesReducer.allVarianceStatuses);
  const {
    currentStockManagementRegister,
    varianceReports,
    fetchingVarianceReportsLoading,
    generatingVarianceReport,
    varianceHistory,
    fetchingVarianceHistoryLoading
  } = useSelector((state) => state.labsStockManagementReducer);
  const dispatch = useDispatch();
  const [sortedInfo, setSortedInfo] = useState({});
  const [editingKey, setEditingKey] = useState("");
  const [editingField, setEditingField] = useState("");
  const [editValue, setEditValue] = useState("");
  const [localData, setLocalData] = useState([]);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [commentsModalVisible, setCommentsModalVisible] = useState(false);
  const [selectedComments, setSelectedComments] = useState([]);
  const [useServerSorting, setUseServerSorting] = useState(false);
  const [updatedRecordIds, setUpdatedRecordIds] = useState(new Set());

  const effectiveRegisterId = registerId || currentStockManagementRegister?.id;

  const reportData = record ? (record.varianceReports || record.variance_reports || []) :
    (registerId ? varianceReports[registerId] || [] : []);

  const transformVarianceData = (data) => {
    return data.map((item, index) => ({
      key: item.id || `temp-${index}`,
      itemCode: item.itemCode || "",
      lotNo: item.lotNo || "",
      expiryDate: item.expiryDate ? dayjs(item.expiryDate).format("YYYY/MM/DD") : "",
      qty: item.qty || 0,
      invNo: item.invNo || "",
      comments: item.comments || [],
      followUpTime: item.followUpTime ? dayjs.utc(item.followUpTime).local().format("YYYY/MM/DD HH:mm") : "",
      status: item.varianceStatus?.statusName || item.status || "",
      attachments: item.attachments || [],
      historyCount: item.historyCount || item._count?.histories || item.histories?.length || 0,
    }));
  };

  useEffect(() => {
    if (reportData.length > 0) {
      setLocalData(transformVarianceData(reportData));
    }
  }, [reportData, effectiveRegisterId]);

  // Update localData when Redux state changes during server-side sorting
  useEffect(() => {
    if (useServerSorting && varianceReports[effectiveRegisterId]) {
      const serverData = varianceReports[effectiveRegisterId] || [];
      setLocalData(transformVarianceData(serverData));
    }
  }, [varianceReports, effectiveRegisterId, useServerSorting]);

  useEffect(() => {
    if (varianceReports[effectiveRegisterId]) {
      const updatedData = varianceReports[effectiveRegisterId] || [];
      setLocalData(transformVarianceData(updatedData));
    }
  }, [varianceReports, effectiveRegisterId]);

  useEffect(() => {
    if (allVarianceStatuses?.length === 0) {
      dispatch(fetchVarianceStatuses());
    }
  }, [allVarianceStatuses?.length, dispatch]);

  // Fetching is now handled in ExpandedRow.jsx to prevent redundant and infinite calls
  // useEffect(() => {
  //   if (effectiveRegisterId) {
  //     dispatch(fetchAllVarianceReports(effectiveRegisterId));
  //   }
  // }, [dispatch, effectiveRegisterId]);

  const handleAmend = () => {
    if (setIsAmending) {
      setIsAmending(true);
    }
  };

  const handleFinalizeStockTake = () => {
    if (registerId) {
      finalizeStockTake(registerId);
    }
  };

  const finalizedTimestamp = finalizedStockTakes?.[registerId];

  const handleHistoryClick = (record) => {
    const reportId = record.id || record.key;
    setSelectedReportId(reportId);
    setHistoryModalVisible(true);
    if (effectiveRegisterId) {
      dispatch(fetchVarianceHistory(effectiveRegisterId, reportId));
    }
  };

  const handleHistoryModalClose = () => {
    setHistoryModalVisible(false);
    setSelectedReportId(null);
  };

  const handleChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
    
    // Use server-side sorting for large datasets (>1000 records)
    const recordCount = reportData.length;
    const useServerSorting = recordCount > 1000;
    
    if (sorter.columnKey && sorter.order && effectiveRegisterId) {
      if (useServerSorting) {
        const sortParams = {
          sortBy: sorter.columnKey,
          sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc'
        };
        dispatch(fetchAllVarianceReports(effectiveRegisterId, sortParams));
        setUseServerSorting(true); 
      } else {
        // Client-side sorting for smaller datasets
        const sortedData = [...data].sort((a, b) => {
          const aValue = a[sorter.columnKey];
          const bValue = b[sorter.columnKey];
          
          if (aValue === undefined || aValue === null) return 1;
          if (bValue === undefined || bValue === null) return -1;
          
          if (sorter.order === 'ascend') {
            return aValue > bValue ? 1 : -1;
          } else {
            return aValue < bValue ? 1 : -1;
          }
        });
        setLocalData(sortedData);
        setUseServerSorting(false);
      }
    } else if (effectiveRegisterId && !useServerSorting) {
      // Reset to original data when no sorting for smaller datasets
      setLocalData(transformVarianceData(reportData));
    }
  };

  const data = (useServerSorting && localData.length === 0) ? 
                    (varianceReports[effectiveRegisterId] || []) : 
                    (localData.length > 0 ? localData : transformVarianceData(reportData));

  
  const isEditing = (record, field) => (record.id || record.key) === editingKey && field === editingField;

  const handleEdit = (record, field) => {
    setEditingKey(record.id || record.key);
    setEditingField(field);
    if (field === "comments") {
      setEditValue("");
    } else if (field === "followUpTime") {
      // Ensure we are parsing a valid date string from the record
      const dateValue = record[field];
      setEditValue(dateValue ? dayjs(dateValue) : null);
    } else if (field === "status") {
      setEditValue(record.varianceStatus?.statusName || "");
    } else {
      setEditValue(record[field] || "");
    }
  };

  const handleCancel = () => {
    setEditingKey("");
    setEditingField("");
    setEditValue("");
  };

  const handleSave = (record) => {
    if (record && effectiveRegisterId) {
      if (editingField === "comments") {
        if (editValue && editValue.trim() !== "") {
          dispatch(addVarianceComment(effectiveRegisterId, record.id || record.key, { content: editValue }));
          setUpdatedRecordIds((prev) => new Set(prev).add(record.id || record.key));
        }
      } else {
        const newData = [...localData];
        const index = newData.findIndex((item) => (item.id || item.key) === (record.id || record.key));

        if (index > -1) {
          const item = newData[index];
          let updatedValue = editValue;
          let updateData = { [editingField]: editValue };

          if (editingField === "status") {
            const selectedStatus = allVarianceStatuses.find(s => s.statusName === editValue);
            if (selectedStatus) {
              updateData = { varianceStatusId: selectedStatus.id }
            }
          }

          newData.splice(index, 1, {
            ...item,
            [editingField]: updatedValue,
          });
          setLocalData(newData);
          setUpdatedRecordIds((prev) => new Set(prev).add(record.id || record.key));
          dispatch(updateVarianceReport(effectiveRegisterId, record.id || record.key, updateData));
        }
      }
      handleCancel();
    }
  };

  const columns = useMemo(() => [
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Stock Code</div>
          </div>
        );
      },
      dataIndex: "itemCode",
      key: "itemCode",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "itemCode" && sortedInfo.order,
      width: "11%",
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Lot No</div>
          </div>
        );
      },
      dataIndex: "lotNo",
      key: "lotNo",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "lotNo" && sortedInfo.order,
      width: "11%",
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Expiry Date</div>
          </div>
        );
      },
      dataIndex: "expiryDate",
      key: "expiryDate",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "expiryDate" && sortedInfo.order,
      width: "10%",
      render: (text) => {
        const color = getExpiryColor(text);
        return color ? (
          <span className={`expiry-highlight expiry-highlight-${color}`}>{text}</span>
        ) : (
          text
        );
      },
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Qty</div>
          </div>
        );
      },
      dataIndex: "qty",
      key: "qty",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "qty" && sortedInfo.order,
      width: "5%",
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Iwt no/ Inv no*</div>
          </div>
        );
      },
      dataIndex: "invNo",
      key: "invNo",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "invNo" && sortedInfo.order,
      width: "10%",
      render: (text, record) => {
        const editable = isEditing(record, "invNo");
        return editable ? (
          <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              style={{ width: "55px !important", height: "23px !important", boxShadow: "none", borderRadius: "2px" }}
              size="small"
              className="stock-input"
            />
            <SaveIcon
              className="custom-icon"
              style={{ cursor: "pointer", width: "15px", height: "15px" }}
              onClick={() => handleSave(record)}
            />
            <Popconfirm
              title="Are you sure you want to cancel?"
              onConfirm={handleCancel}
              okText="Yes"
              cancelText="No"
            >
              <CrossIcon
                className="custom-icon"
                style={{ cursor: "pointer", width: "18px", height: "18px" }}
              />
            </Popconfirm>
          </div>
        ) : (
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <span>{text}</span>
            <EditIcon
              className="custom-icon"
              style={{
                cursor: isAmending ? "not-allowed" : "pointer",
                opacity: isAmending ? 0.5 : 1,
              }}
              onClick={() => !isAmending && handleEdit(record, "invNo")}
            />
          </div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Comments</div>
          </div>
        );
      },
      dataIndex: "comments",
      key: "comments",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "comments" && sortedInfo.order,
      width: "28%",
      render: (text, record) => {
        const editable = isEditing(record, "comments");
        const commentsList = Array.isArray(record.comments) ? record.comments : [];

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "0px" }}>
            {editable ? (
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <Input
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onPressEnter={() => handleSave(record)}
                  placeholder="Type a Message"
                  className="stock-input"
                  style={{ width: "100%", height: "24px", boxShadow: "none", borderRadius: "2px", borderColor: "#D9D9D9" }}
                  suffix={
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <img
                        src={SendIcon}
                        alt="send"
                        style={{ cursor: "pointer", width: "16px", height: "16px" }}
                        onClick={() => handleSave(record)}
                      />
                      <Popconfirm
                        title="Are you sure you want to cancel?"
                        onConfirm={handleCancel}
                        okText="Yes"
                        cancelText="No"
                      >
                        <CrossIcon
                          className="custom-icon"
                          style={{ cursor: "pointer", width: "16px", height: "16px" }}
                        />
                      </Popconfirm>
                    </div>
                  }
                />
              </div>
            ) : (
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <Button
                  icon={<PlusOutlined />}
                  className="add-doc-btn"
                  size="middle"
                  disabled={isAmending}
                  onClick={() => handleEdit(record, "comments")}
                >
                  COMMENT
                </Button>
              </div>
            )}

            {commentsList.slice().reverse().slice(0, 2).map((comment, index) => (
              <div
                key={index}
                style={{
                  border: "1px solid #D9D9D9",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  backgroundColor: "#fff",
                  marginTop: "4px",
                  width: "100%"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
                  <Avatar
                    size={18}
                    style={{
                      backgroundColor: index % 2 === 0 ? "#A5D6A7" : "#BBDEFB",
                      color: "#101820",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {typeof comment.user === 'string' ? comment.user.charAt(0) : (comment.user?.firstName?.charAt(0) || "U")}
                  </Avatar>
                  <span style={{ fontSize: "12px", fontWeight: "500", color: "#101820" }}>
                    {typeof comment.user === 'string' ? comment.user : `${comment.user?.firstName || ""} ${comment.user?.lastName || ""}`.trim() || "Unknown User"}
                  </span>
                  <span style={{ fontSize: "12px", color: "#101820" }}>
                    - {comment.date || (comment.createdAt ? dayjs(comment.createdAt).format("YYYY/MM/DD") : "")} {comment.time || (comment.createdAt ? dayjs(comment.createdAt).format("HH:mm") : "")}
                  </span>
                </div>
                <div style={{ fontSize: "14px", color: "#383838", paddingTop: "8px" }}>
                  {comment.text || comment.content}
                </div>
              </div>
            ))}

            {commentsList.length > 2 && (
              <Button
                type="link"
                onClick={() => {
                  setSelectedComments(commentsList);
                  setCommentsModalVisible(true);
                }}
                style={{
                  padding: "4px 0",
                  marginLeft: "8px",
                  fontSize: "12px",
                  fontFamily: "poppins, Arial",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  color: "#101820",
                  fontWeight: "500",
                  textDecoration: "underline"
                }}
              >
                See All Comments
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Follow Up Time</div>
          </div>
        );
      },
      dataIndex: "followUpTime",
      key: "followUpTime",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "followUpTime" && sortedInfo.order,
      width: "19%",
      render: (text, record) => {
        const editable = isEditing(record, "followUpTime");
        return editable ? (
          <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
            <DatePicker
              showTime
              value={editValue}
              onChange={(date) => setEditValue(date)}
              format="YYYY/MM/DD HH:mm"
              style={{ width: "135px", height: "24px", borderRadius: "2px", padding: "0" }}
              size="small"
              allowClear={false}
              suffixIcon={null}
              prefix={null}
              className="follow-up-picker"
            />
            {editValue && (
              <SaveIcon
                className="custom-icon"
                style={{ cursor: "pointer", width: "15px", height: "15px" }}
                onClick={() => handleSave(record)}
              />
            )}
            <Popconfirm
              title="Are you sure you want to cancel?"
              onConfirm={handleCancel}
              okText="Yes"
              cancelText="No"
            >
              <CrossIcon
                className="custom-icon"
                style={{ cursor: "pointer", width: "18px", height: "18px" }}
              />
            </Popconfirm>
          </div>
        ) : record.followUpTime ? (
          <div style={{ display: "flex", gap: "10px", alignItems: "center", width: "max-content" }}>
            <span>{dayjs(record.followUpTime).format("YYYY/MM/DD HH:mm")}</span>
            <EditIcon
              className="custom-icon"
              style={{
                cursor: isAmending ? "not-allowed" : "pointer",
                opacity: isAmending ? 0.5 : 1,
                width: "15px",
                height: "15px"
              }}
              onClick={() => !isAmending && handleEdit(record, "followUpTime")}
            />
          </div>
        ) : (
          <Button
            icon={<PlusOutlined />}
            className="add-doc-btn"
            size="middle"
            disabled={isAmending}
            onClick={() => handleEdit(record, "followUpTime")}
          >
            FOLLOW UP
          </Button>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Status*</div>
          </div>
        );
      },
      dataIndex: "status",
      key: "status",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "status" && sortedInfo.order,
      width: "17%",
      render: (text, record) => {
        const editable = isEditing(record, "status");
        const statusName = record.varianceStatus?.statusName || text;
        const statusColor = record.varianceStatus?.color || allVarianceStatuses.find(s => s.statusName === statusName)?.color || "#d9d9d9";

        return editable ? (
          <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
            <Select
              value={editValue || undefined}
              onChange={(value) => setEditValue(value)}
              placeholder="Select Status"
              style={{ width: "70%", height: "24px", borderRadius: "2px" }}
              size="small"
              className="stock-input"
              dropdownStyle={{ minWidth: "140px" }}
            >
              {allVarianceStatuses.map((status) => (
                <Select.Option key={status.id} value={status.statusName}>
                  {status.statusName}
                </Select.Option>
              ))}
            </Select>
            <SaveIcon
              className="custom-icon"
              style={{ cursor: "pointer", width: "20%", height: "15px" }}
              onClick={() => handleSave(record)}
            />
            <Popconfirm
              title="Are you sure you want to cancel?"
              onConfirm={handleCancel}
              okText="Yes"
              cancelText="No"
            >
              <CrossIcon
                className="custom-icon"
                style={{ cursor: "pointer", width: "20%", height: "18px" }}
              />
            </Popconfirm>
          </div>
        ) : statusName ? (
          <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
            <Tag
              color={statusColor}
              style={{
                color: "#383838",
                width: "100px",
                height: "20px",
                borderRadius: "10px",
                textAlign: "center",
                fontSize: "12px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                border: 'none',
              }}
            >
              {statusName}
            </Tag>
            <EditIcon
              className="custom-icon"
              style={{
                cursor: isAmending ? "not-allowed" : "pointer",
                opacity: isAmending ? 0.5 : 1,
                width: "15px",
                height: "15px"
              }}
              onClick={() => !isAmending && handleEdit(record, "status")}
            />
          </div>
        ) : (
          <Button
            icon={<PlusOutlined />}
            className="add-doc-btn"
            size="middle"
            disabled={isAmending}
            onClick={() => handleEdit(record, "status")}
          >
            STATUS
          </Button>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Attach</div>
          </div>
        );
      },
      dataIndex: "attach",
      key: "attach",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "attach" && sortedInfo.order,
      width: "9%",
      render: (text, record) => (
        <>
          <AttachmentContainer
            recordType="variance_report"
            record={record}
            id={record?.key}
            registerId={effectiveRegisterId}
            color="#ed017f"
            disabled={isAmending}
          />
        </>
      ),
    },
    {
      title: () => {
        return (
          <div align="center" className="select-parent-div">
            <div className="stock-table-header-text"></div>
          </div>
        );
      },
      dataIndex: "history",
      key: "history",
      width: "8%",
      render: (text, record) => {
        const hasHistory = record.historyCount > 0 || record.comments?.length > 0 || record.followUpTime || record.status || updatedRecordIds.has(record.id || record.key);

        return hasHistory ? (
          <HistoryOutlined
            style={{ cursor: "pointer", fontSize: "16px", color: "#ed017f" }}
            onClick={(e) => {
              e.stopPropagation();
              handleHistoryClick(record);
            }}
          />
        ) : null;
      },
    },
  ], [sortedInfo, isAmending, allVarianceStatuses, editValue, editingKey, editingField, effectiveRegisterId, varianceHistory, fetchingVarianceHistoryLoading, updatedRecordIds]);

  return (
    <Row className="stock-doc-control-parent-row">
      <Col span={24} className="stock-table-heading-col">
        <Row align="middle" gutter={[50, 0]} justify="space-between">
          <Col>
            <Row gutter={[50, 0]} align="middle">
              <Col>
                <div>
                  <span className="heading-span">Variance Report</span>
                </div>
              </Col>
              {finalizedTimestamp ? (
                <Col>
                  <span className="heading-span">
                    Stock Take finalized - {dayjs(finalizedTimestamp).format('DD MMM YYYY  HH:mm ')}
                  </span>
                </Col>
              ) : (
                <>
                  <Col>
                    <Popconfirm
                      title={
                        <>
                          <div>Are you sure you want to finalize this Stock Take?</div>
                          <div style={{ textAlign: 'left' }}>Please note this action cannot be undone.</div>
                        </>
                      }
                      onConfirm={handleFinalizeStockTake}
                      okText="Yes"
                      cancelText="No"
                      placement="bottom"
                    >
                      <Button
                        className="add-doc-btn"
                        size="middle"
                        style={{ width: "180px" }}
                        disabled={isAmending}
                      >
                        Finalize Stock Take
                      </Button>
                    </Popconfirm>
                  </Col>
                  <Col>
                    <Button
                      className="add-doc-btn"
                      size="middle"
                      style={{ width: "180px" }}
                      onClick={handleAmend}
                      disabled={isAmending}
                    >
                      Amend Stock Take
                    </Button>
                  </Col>
                </>
              )}
            </Row>
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <Table
          loading={generatingVarianceReport || fetchingVarianceReportsLoading}
          scroll={{ y: '350px' }}
          className={`special-table stock-table ims-doc-table reduced-padding-table`}
          rowClassName="editable-row"
          columns={columns}
          dataSource={data}
          pagination={false}
          rowKey={(record) => record.id || record.key}
          onChange={handleChange}
        />
      </Col>
      <VarianceHistoryModal
        visible={historyModalVisible}
        onClose={handleHistoryModalClose}
        history={selectedReportId ? (varianceHistory[selectedReportId] || []) : []}
        loading={fetchingVarianceHistoryLoading}
        title="History"
      />
      <CommentsModal
        visible={commentsModalVisible}
        onClose={() => setCommentsModalVisible(false)}
        comments={selectedComments}
        loading={false}
        title="Comments"
      />
    </Row>
  );
};

export default VarianceReportTable;